import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft } from "@phosphor-icons/react";

const STEPS = [
  {
    target: '[data-testid="healthspan-card"]',
    title: "Your Healthspan Score",
    body: "One number (0–1000) for how well you're sleeping, moving, eating and recovering. Watch it climb as your habits improve.",
  },
  {
    target: '[data-testid="open-checkin-btn"]',
    title: "Start every day here",
    body: "A 30-second check-in tells Ikavio how you feel today — then it plans your optimal workout, food and recovery around it.",
  },
  {
    target: '[data-testid="tab-coach"], [data-testid="bottom-tab-coach"]',
    title: "Meet your AI coach",
    body: "Ask anything — training, food, sleep, stress. Answers adapt to your data and your experience level.",
  },
  {
    target: '[data-testid="glossary-link-btn"]',
    title: "Never get lost in jargon",
    body: "Any term unclear? Tap the book for the glossary — HRV, biological age, all of it explained in plain language.",
  },
];

const PAD = 10;

export default function GuidedTour({ userId }) {
  const key = `vitalis-tour-done-${userId}`;
  const [step, setStep] = useState(-1);
  const [rect, setRect] = useState(null);
  const scrolledFor = useRef(-1);

  useEffect(() => {
    if (!userId || localStorage.getItem(key)) return;
    const t = setTimeout(() => setStep(0), 900);
    return () => clearTimeout(t);
  }, [userId, key]);

  const measure = useCallback(() => {
    if (step < 0) return;
    const el = Array.from(document.querySelectorAll(STEPS[step].target)).find((e) => e.offsetParent !== null);
    if (!el) {
      setRect(null);
      return;
    }
    if (scrolledFor.current !== step) {
      el.scrollIntoView({ block: "center" });
      scrolledFor.current = step;
    }
    const r = el.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, [step]);

  useEffect(() => {
    measure();
    window.addEventListener("resize", measure);
    // the target may live on another tab — keep looking so the tour never dims a blank screen
    const poll = setInterval(measure, 1200);
    return () => {
      window.removeEventListener("resize", measure);
      clearInterval(poll);
    };
  }, [measure]);

  const finish = () => {
    localStorage.setItem(key, "1");
    setStep(-1);
  };
  const next = () => (step >= STEPS.length - 1 ? finish() : setStep(step + 1));

  // Nothing to spotlight on this view (e.g. the profile tab) — stay out of the way.
  if (step < 0 || !rect) return null;
  const s = STEPS[step];

  const CARD_W = 340, CARD_H = 250;
  let cardTop = "40%", cardLeft = "50%";
  if (rect) {
    const vh = window.innerHeight, vw = window.innerWidth;
    if (rect.top + rect.height + CARD_H + 24 < vh) cardTop = rect.top + rect.height + 22;
    else if (rect.top - CARD_H - 22 > 0) cardTop = rect.top - CARD_H - 22;
    else cardTop = Math.max(16, Math.min(vh - CARD_H - 16, rect.top + 40));
    cardLeft = Math.min(Math.max(rect.left, 16), vw - CARD_W - 16);
    const overlapsTarget = cardTop >= rect.top - CARD_H && cardTop <= rect.top + rect.height;
    if (overlapsTarget && rect.left + rect.width + CARD_W + 32 < vw) {
      cardLeft = rect.left + rect.width + 22;
    }
  }

  return (
    <div className="fixed inset-0 z-[60]" data-testid="guided-tour">
      {/* click anywhere outside the card to dismiss — never trap the user */}
      <button
        aria-label="Dismiss tour"
        data-testid="tour-backdrop"
        onClick={finish}
        className="absolute inset-0 w-full h-full cursor-default"
      />
      {/* spotlight */}
      {rect && (
        <motion.div
          layout
          className="absolute rounded-3xl pointer-events-none"
          style={{
            top: rect.top - PAD,
            left: rect.left - PAD,
            width: rect.width + PAD * 2,
            height: rect.height + PAD * 2,
            boxShadow: "0 0 0 9999px rgba(28,25,23,0.6)",
            border: "2px solid var(--brand)",
            transition: "all 0.35s cubic-bezier(0.16,1,0.3,1)",
          }}
        />
      )}

      {/* card */}
      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="absolute bg-surface rounded-3xl shadow-xl border border-border p-6 w-[340px] max-w-[calc(100vw-32px)]"
          style={{ top: cardTop, left: cardLeft }}
          data-testid="tour-card"
        >
          <div className="label-eyebrow">
            Quick tour — {step + 1} of {STEPS.length}
          </div>
          <h3 className="font-display text-lg font-medium mt-2" data-testid="tour-title">{s.title}</h3>
          <p className="text-sm text-muted2 mt-1.5 leading-relaxed">{s.body}</p>

          <div className="flex items-center gap-1.5 mt-4" data-testid="tour-step-dots">
            {STEPS.map((_, i) => (
              <span key={i} className={`h-1.5 rounded-full transition-all ${i === step ? "w-5 bg-brand" : "w-1.5 bg-border"}`} />
            ))}
          </div>

          <div className="flex items-center justify-between mt-5">
            <button data-testid="tour-skip-btn" onClick={finish} className="text-xs text-muted2 hover:text-ink transition-colors">
              Skip tour
            </button>
            <div className="flex items-center gap-2">
              {step > 0 && (
                <button
                  data-testid="tour-back-btn"
                  onClick={() => setStep(step - 1)}
                  className="w-9 h-9 rounded-full border border-border flex items-center justify-center hover:bg-secondary transition-colors"
                >
                  <ArrowLeft size={15} weight="bold" />
                </button>
              )}
              <button
                data-testid="tour-next-btn"
                onClick={next}
                className="inline-flex items-center gap-1.5 bg-brand text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-ink transition-colors"
              >
                {step >= STEPS.length - 1 ? "Let's go" : "Next"}
                <ArrowRight size={14} weight="bold" />
              </button>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
